import {
  API_ROUTES,
  API_ROUTE_PREFIX_HTTPS,
  API_ROUTE_V2_PREFIX_HTTPS,
  EditTravellerPayload,
  EndorsementResponse,
  ICheckCheckoutEligibilityRequestParams,
  IEditTravellersRequestParams,
  IGetNextPageRouteRequestParams,
  IGetSuggestionsInfoRequestParams,
  IOrderDetailsRequestParams,
  IPageRequestParams,
} from "@tm/api-utils";
import { CONTEXT, LOB, PARTNER, PLATFORM } from "@tm/isomorphic-utils";
import {
  getHTTPSRoutePrefixOnClientSide,
  getLobOnClientSide,
  getPartnerOnClientSide,
  getPathPartnerOnClientSide,
  setURLQueryParams,
} from "../utils";
import { getDeviceIdForClient } from "../utils/getDeviceId";
import { API_HTTPS } from "./config";

export const paymentCheckout = async (reqData: any): Promise<any> => {
  try {
    const { partner = getPartnerOnClientSide(), data, query } = reqData;
    const params = {
      context: CONTEXT.STANDALONE_V2,
      ins_new_flow: true,
      lob: getLobOnClientSide(partner),
      itineraryId: data?.query?.itineraryId,
    };
    const urlParams = setURLQueryParams(params);
    const dvid = getDeviceIdForClient();

    const apiRoutePrefix = getHTTPSRoutePrefixOnClientSide(partner);

    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.post(
      `${apiRoutePrefix}/${pathPartner}/${API_ROUTES.PAYMENT_CHECKOUT}${
        urlParams ? `?${urlParams}` : ""
      }`,
      data,
      {
        headers: {
          cmp: sessionStorage.getItem("cmp") || "fallback",
          dvid: dvid || "",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error", { error });
    throw error;
  }
};

export const sendPolicyEmail = async (data: any): Promise<any> => {
  try {
    const { bookingId, partner, query } = data;
    const urlParams = setURLQueryParams({ bookingID: bookingId, ...query });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.TRIGGER_EMAIL}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp: sessionStorage.getItem("cmp") || "fallback",
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error: any) {
    console.log("Error", { error: error.message });
    throw error;
  }
};

export const editInsuranceDetails = async (
  reqData: IEditTravellersRequestParams
): Promise<any> => {
  try {
    const {
      bookingID,
      partner = getPartnerOnClientSide(),
      data = [],
    } = reqData;

    if (!bookingID)
      throw "editInsuranceDetails: Booking id not present or invalid";

    const urlParams = setURLQueryParams({ bookingID });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.put(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.EDIT_INSURANCE_DETAILS
      }${urlParams ? `?${urlParams}` : ""}`,
      data,
      {
        timeout: 30000,
        headers: {
          cmp: sessionStorage.getItem("cmp") || "fallback",
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error) {
    console.error("Error", { error });
    throw error;
  }
};

export const submitEndorsement = async (reqData: {
  bookingId: string;
  payload: EditTravellerPayload;
  partner?: string;
  query?: Record<string, string | string[] | undefined>;
}): Promise<EndorsementResponse> => {
  try {
    const {
      partner = getPartnerOnClientSide(),
      bookingId,
      payload,
      query,
    } = reqData;
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(
      partner as Parameters<typeof getPathPartnerOnClientSide>[0]
    );
    const urlParams = setURLQueryParams({ bookingId, ...query });
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.EDIT_TRAVELLER_V2
      }${urlParams ? `?${urlParams}` : ""}`,
      payload,
      {
        headers: {
          cmp:
            (typeof window !== "undefined" && sessionStorage.getItem("cmp")) ||
            "fallback",
          dvid: dvid || "",
        },
      }
    );

    return response?.data;
  } catch (error) {
    console.error("Error submitting endorsement", { error });
    throw error;
  }
};

export const sendPolicyEmailV2 = async (data: any): Promise<any> => {
  try {
    const { bookingId, partner, query } = data;
    const urlParams = setURLQueryParams({ bookingId, ...query });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.SEND_POLICY_EMAIL
      }${urlParams ? `?${urlParams}` : ""}`,
      {
        headers: {
          cmp: sessionStorage.getItem("cmp") || "fallback",
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error: any) {
    console.log("Error", { error: error.message });
    throw error;
  }
};

export const getNextPageRouteDetails = async (
  reqData: IGetNextPageRouteRequestParams
) => {
  const { partner = PARTNER.MMT, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();

  let url;
  if (partner === PARTNER.MYPARTNER || partner === PARTNER.MYBIZ) {
    url = `${API_ROUTE_V2_PREFIX_HTTPS}/mmt/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  } else {
    url = `${API_ROUTE_PREFIX_HTTPS}/${partner}/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  }

  const response = await API_HTTPS.post(url, data, {
    headers: {
      dvid: dvid || "",
      cmp:
        (typeof window !== "undefined" && sessionStorage.getItem("cmp")) ||
        query?.["cmp"] ||
        "fallback",
    },
  });

  if (partner === PARTNER.MYPARTNER || partner === PARTNER.MYBIZ) {
    return {
      responseStatus: response.data?.responseStatus,
      nextPageRouteConfig: response.data?.data?.nextPageRouteConfig,
    };
  }
  return response.data;
};

export const hitRetailConsentMyPartner = async (
  reqData: IPageRequestParams
) => {
  const { partner = PARTNER.MYPARTNER, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.HIT_RETAIL_CONSENT}?${urlParams}`;
  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getOrdersMyPartner = async (reqData: IPageRequestParams) => {
  const { partner = PARTNER.MYPARTNER, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.GET_ORDERS_RETAIL_MYPARTNER}?${urlParams}`;
  const response = await API_HTTPS.get(url, {
    params: data,
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const checkCheckoutEligibility = async (
  reqData: ICheckCheckoutEligibilityRequestParams
) => {
  const { partner = PARTNER.MMT, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: LOB.STANDALONE,
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${partner}/${API_ROUTES.CHECK_CHECKOUT_ELIGIBILITY}?${urlParams}`;

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getSuggestionsInfo = async (
  reqData: IGetSuggestionsInfoRequestParams
) => {
  const { query, ...data } = reqData;

  const params = {
    lob: LOB.STANDALONE_MYBIZ,
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    partner: PARTNER.MYBIZ,
    ...query,
  };
  const dvid = getDeviceIdForClient();

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_V2_PREFIX_HTTPS}/mmt/${API_ROUTES.GET_SUGGESTIONS_INFO}?${urlParams}`;

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getPolicyStatus = async (
  reqData: IOrderDetailsRequestParams
): Promise<{
  responseStatus: string;
  message: string;
  data: {
    policyStatus:
      | "GENERATING_POLICY"
      | "GENERATING_POLICY_SUCCESS"
      | "GENERATING_POLICY_FAILED"
      | "UPDATING_POLICY"
      | "UPDATING_POLICY_SUCCESS"
      | "UPDATING_POLICY_FAILED";
    continuePolling: boolean;
  };
}> => {
  try {
    const { partner = getPartnerOnClientSide(), bookingId, query } = reqData;
    const dvid = getDeviceIdForClient();
    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      bookingId,
      ...query,
    };

    const urlParams = setURLQueryParams(params);
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.get(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.POLICY_STATUS_V2}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp:
            (typeof window !== "undefined" && sessionStorage.getItem("cmp")) ||
            reqData.cmp ||
            "fallback",
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching policy status", { error });
    throw error;
  }
};

export const getOrderDetailsV2 = async (
  reqData: IOrderDetailsRequestParams
): Promise<any> => {
  try {
    const { partner = getPartnerOnClientSide(), bookingId, query } = reqData;
    const dvid = getDeviceIdForClient();
    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      bookingId,
      ...query,
    };

    const urlParams = setURLQueryParams(params);
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.get(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.ORDER_DETAIL_V2}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp:
            (typeof window !== "undefined" && sessionStorage.getItem("cmp")) ||
            reqData.cmp ||
            "fallback",
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching order details", { error });
    throw error;
  }
};
